import { useId, type HTMLAttributes, type Ref } from 'react'
import { cx, formatNumber } from '@/shared/lib'
import s from './ProgressBar.module.css'

/**
 * Смысл состояния.
 * `accent` — идёт; `success` — завершено; `warning` — доля подошла к порогу;
 * `danger` — оборвалось, заливка остаётся на достигнутом месте.
 */
export type ProgressBarTone = 'accent' | 'success' | 'warning' | 'danger'

/** Ступени шкалы: xs — внутри строки файла, sm — самостоятельная полоса под шапкой блока. */
export type ProgressBarSize = 'xs' | 'sm'

export type ProgressBarProps = {
  /** Текущее значение в единицах `max`: байты, номер шага, баллы. Приходит готовым с бэкенда. */
  value: number
  /** Верхняя граница. 100 — проценты, 5 — «шаг 3 из 5», 12480 — строки прайса. */
  max?: number
  tone?: ProgressBarTone
  size?: ProgressBarSize
  /** Видимая подпись слева и одновременно доступное имя полосы. */
  label?: string
  /** Показать справа процент, посчитанный через Intl. Кит рисует там ровно процент. */
  showValue?: boolean
  /**
   * Формулировка доли для ассистивных технологий: «Шаг 3 из 5», «3 400 из 5 000 баллов».
   * Уходит в `aria-valuetext` и не показывается глазом — видимая формулировка живёт рядом
   * (чип над полосой, строка подсказок под ней). Составляет её потребитель через i18n.
   */
  valueText?: string
  /** Долю уже назвал сосед или родитель: полоса уходит из дерева доступности. */
  decorative?: boolean
  ref?: Ref<HTMLDivElement>
} & Omit<HTMLAttributes<HTMLDivElement>, 'children'>

// Карта классов при noUncheckedIndexedAccess приходит как string | undefined
const TONE_CLASS: Record<ProgressBarTone, string | undefined> = {
  accent: s.toneAccent,
  success: s.toneSuccess,
  warning: s.toneWarning,
  danger: s.toneDanger,
}

const SIZE_CLASS: Record<ProgressBarSize, string | undefined> = {
  xs: s.xs,
  sm: s.sm,
}

/** Работа в процентах — случай по умолчанию и запасной знаменатель для мусорного `max`. */
const DEFAULT_MAX = 100

/**
 * Линейная полоса детерминированного прогресса: загрузка файла, шаг мастера, уровень клиента.
 *
 * Неопределённой загрузки здесь нет намеренно: «идёт, доля неизвестна» — это Spinner,
 * а бегущая полоса поля ввода живёт внутри самого Input.
 * Контракт: docs/ui-kit/contracts/progress-bar.md
 */
export function ProgressBar({
  value,
  max = DEFAULT_MAX,
  tone = 'accent',
  size = 'sm',
  label,
  showValue = false,
  valueText,
  decorative = false,
  className,
  ref,
  ...rest
}: ProgressBarProps) {
  const reactId = useId()
  const hasLabel = Boolean(label)
  const labelId = hasLabel ? `${reactId}-label` : undefined

  /*
   * Мусор с бэкенда (loaded > total, -1 вместо «не считается», null → NaN, max = 0 у пустого
   * мастера) не имеет права нарисовать заливку в 12 000 % или дать деление на ноль.
   * Тот же приём, что у CounterBadge: значение приводится к диапазону, а не проверяется вызовом.
   */
  const safeMax = Number.isFinite(max) && max > 0 ? max : DEFAULT_MAX
  const safeValue = Number.isFinite(value) ? Math.min(Math.max(value, 0), safeMax) : 0
  const ratio = safeValue / safeMax

  // Два знака после запятой: в DOM не должна попадать ширина «43.71827491%»
  const width = `${Math.round(ratio * 10000) / 100}%`

  /*
   * Роль стоит на корне, а не на треке: aria-label и aria-labelledby, переданные снаружи,
   * обязаны попасть на элемент с ролью. Оба атрибута дополняются, а не затираются —
   * свой label и свой valueText имеют приоритет, внешние остаются, когда пропов нет.
   */
  const a11y: HTMLAttributes<HTMLDivElement> = decorative
    ? { 'aria-hidden': true }
    : {
        role: 'progressbar',
        'aria-valuemin': 0,
        'aria-valuemax': safeMax,
        'aria-valuenow': safeValue,
        'aria-valuetext': valueText ?? rest['aria-valuetext'],
        'aria-labelledby': labelId ?? rest['aria-labelledby'],
      }

  return (
    <div
      {...rest}
      {...a11y}
      ref={ref}
      className={cx(s.root, SIZE_CLASS[size], TONE_CLASS[tone], className)}
      data-tone={tone}
      data-size={size}
    >
      {hasLabel || showValue ? (
        <div className={s.caption}>
          {hasLabel ? (
            <span className={s.label} id={labelId}>
              {label}
            </span>
          ) : null}
          {showValue ? (
            <span className={s.value}>{formatNumber(ratio, { style: 'percent' })}</span>
          ) : null}
        </div>
      ) : null}

      <div className={s.track}>
        {/* Ширина — единственная вычисляемая величина, поэтому инлайн-стиль */}
        <span className={cx(s.fill, ratio > 0 && s.fillStarted)} style={{ width }} />
      </div>
    </div>
  )
}
